export interface PropertyStructureConfig {
  roadNumber: boolean;
  block: boolean;
  tower: boolean;
  flat: boolean;
  house: boolean;
  plot: boolean;
}

export interface CustomPropertyTerminology {
  singular?: string;
  plural?: string;
  roadLabel?: string;
  blockLabel?: string;
  towerLabel?: string;
  flatLabel?: string;
  houseLabel?: string;
  plotLabel?: string;
}

export const DEFAULT_PROPERTY_STRUCTURES: Record<string, PropertyStructureConfig> = {
  FLATS: {
    roadNumber: false,
    block: true,
    tower: true,
    flat: true,
    house: false,
    plot: false,
  },
  APARTMENTS: {
    roadNumber: false,
    block: true,
    tower: true,
    flat: true,
    house: false,
    plot: false,
  },
  VILLAS: {
    roadNumber: true,
    block: true,
    tower: false,
    flat: false,
    house: true,
    plot: false,
  },
  HOUSES: {
    roadNumber: true,
    block: true,
    tower: false,
    flat: false,
    house: true,
    plot: false,
  },
  OPEN_PLOTS: {
    roadNumber: true,
    block: true,
    tower: false,
    flat: false,
    house: false,
    plot: true,
  },
  PLOTS: {
    roadNumber: true,
    block: true,
    tower: false,
    flat: false,
    house: false,
    plot: true,
  },
  FARM_PLOTS: {
    roadNumber: true,
    block: false,
    tower: false,
    flat: false,
    house: false,
    plot: true,
  },
  FARM_HOUSES: {
    roadNumber: true,
    block: false,
    tower: false,
    flat: false,
    house: true,
    plot: true,
  },
  GATED_COMMUNITY: {
    roadNumber: true,
    block: true,
    tower: true,
    flat: true,
    house: true,
    plot: false,
  },
  MIXED: {
    roadNumber: true,
    block: true,
    tower: true,
    flat: true,
    house: true,
    plot: true,
  },
  MIXED_COMMUNITY: {
    roadNumber: true,
    block: true,
    tower: true,
    flat: true,
    house: true,
    plot: true,
  },
  OTHER: {
    roadNumber: true,
    block: true,
    tower: false,
    flat: true,
    house: true,
    plot: true,
  },
};

export function normalizeSocietyType(type?: string | null): string {
  if (!type) return 'FLATS';
  const upper = String(type).toUpperCase().trim().replace(/\s+/g, '_');
  if (upper === 'VILLA') return 'VILLAS';
  if (upper === 'HOUSE') return 'HOUSES';
  if (upper === 'FLAT') return 'FLATS';
  if (upper === 'APARTMENT') return 'APARTMENTS';
  if (upper === 'PLOT' || upper === 'OPEN_PLOT') return 'OPEN_PLOTS';
  if (upper === 'FARM_PLOT') return 'FARM_PLOTS';
  if (upper === 'FARM_HOUSE') return 'FARM_HOUSES';
  return upper;
}

export function getPropertyStructure(
  societyType?: string | null,
  customOverrides?: Partial<PropertyStructureConfig> | null,
): PropertyStructureConfig {
  const normType = normalizeSocietyType(societyType);
  const base = DEFAULT_PROPERTY_STRUCTURES[normType] || DEFAULT_PROPERTY_STRUCTURES.FLATS;
  if (!customOverrides || typeof customOverrides !== 'object') {
    return { ...base };
  }
  return {
    roadNumber: typeof customOverrides.roadNumber === 'boolean' ? customOverrides.roadNumber : base.roadNumber,
    block: typeof customOverrides.block === 'boolean' ? customOverrides.block : base.block,
    tower: typeof customOverrides.tower === 'boolean' ? customOverrides.tower : base.tower,
    flat: typeof customOverrides.flat === 'boolean' ? customOverrides.flat : base.flat,
    house: typeof customOverrides.house === 'boolean' ? customOverrides.house : base.house,
    plot: typeof customOverrides.plot === 'boolean' ? customOverrides.plot : base.plot,
  };
}

export interface ResolvedPropertyTerminology {
  singular: string;
  plural: string;
  numberLabel: string;
  addressLabel: string;
  addLabel: string;
  searchPlaceholder: string;
  dashboardTitle: string;

  // Specific entity labels
  roadLabel: string;
  blockLabel: string;
  towerLabel: string;
  flatLabel: string;
  houseLabel: string;
  plotLabel: string;

  // Granular applicability flags
  isRoadApplicable: boolean;
  isBlockApplicable: boolean;
  isTowerApplicable: boolean;
  isFlatApplicable: boolean;
  isHouseApplicable: boolean;
  isPlotApplicable: boolean;
  isBlockOrTowerApplicable: boolean;
}

export function resolvePropertyTerminology(
  societyType?: string | null,
  structureConfig?: PropertyStructureConfig | null,
  customTerminology?: CustomPropertyTerminology | null,
): ResolvedPropertyTerminology {
  const normType = normalizeSocietyType(societyType);
  const structure = structureConfig || getPropertyStructure(normType);

  let defaultSingular = 'Property';
  let defaultPlural = 'Properties';
  let defaultNumber = 'Property Number';
  let defaultAddress = 'Address / Unit';

  if (normType === 'FLATS' || normType === 'APARTMENTS') {
    defaultSingular = 'Flat';
    defaultPlural = 'Flats';
    defaultNumber = 'Flat Number';
    defaultAddress = 'Tower / Flat';
  } else if (normType === 'VILLAS') {
    defaultSingular = 'Villa';
    defaultPlural = 'Villas';
    defaultNumber = 'Villa Number';
    defaultAddress = 'Road / Villa';
  } else if (normType === 'HOUSES') {
    defaultSingular = 'House';
    defaultPlural = 'Houses';
    defaultNumber = 'House Number';
    defaultAddress = 'Street / House';
  } else if (normType === 'OPEN_PLOTS' || normType === 'PLOTS' || normType === 'FARM_PLOTS') {
    defaultSingular = normType === 'FARM_PLOTS' ? 'Farm Plot' : 'Plot';
    defaultPlural = normType === 'FARM_PLOTS' ? 'Farm Plots' : 'Plots';
    defaultNumber = 'Plot Number';
    defaultAddress = 'Sector / Survey / Plot';
  } else if (normType === 'FARM_HOUSES') {
    defaultSingular = 'Farm House';
    defaultPlural = 'Farm Houses';
    defaultNumber = 'Farm House Number';
    defaultAddress = 'Zone / Farm House';
  } else if (normType === 'GATED_COMMUNITY') {
    if (structure.flat && !structure.house && !structure.plot) {
      defaultSingular = 'Flat';
      defaultPlural = 'Flats';
      defaultNumber = 'Flat Number';
      defaultAddress = 'Building / Flat';
    } else if (structure.house && !structure.flat && !structure.plot) {
      defaultSingular = 'House / Villa';
      defaultPlural = 'Houses / Villas';
      defaultNumber = 'House Number';
      defaultAddress = 'Road / House';
    } else if (structure.plot && !structure.flat && !structure.house) {
      defaultSingular = 'Plot';
      defaultPlural = 'Plots';
      defaultNumber = 'Plot Number';
      defaultAddress = 'Sector / Plot';
    } else {
      defaultSingular = 'Property';
      defaultPlural = 'Properties';
      defaultNumber = 'Property Number';
      defaultAddress = 'Road / Block / Unit';
    }
  }

  const singular = customTerminology?.singular?.trim() || defaultSingular;
  const plural = customTerminology?.plural?.trim() || defaultPlural;

  return {
    singular,
    plural,
    numberLabel: customTerminology?.singular ? `${customTerminology.singular} Number` : defaultNumber,
    addressLabel: defaultAddress,
    addLabel: `Add ${singular}`,
    searchPlaceholder: `Search ${plural.toLowerCase()}...`,
    dashboardTitle: `${plural} Overview`,

    roadLabel: customTerminology?.roadLabel?.trim() || 'Road / Street No.',
    blockLabel: customTerminology?.blockLabel?.trim() || 'Block / Sector',
    towerLabel: customTerminology?.towerLabel?.trim() || 'Tower / Building',
    flatLabel: customTerminology?.flatLabel?.trim() || 'Flat',
    houseLabel: customTerminology?.houseLabel?.trim() || 'House / Villa',
    plotLabel: customTerminology?.plotLabel?.trim() || 'Plot',

    isRoadApplicable: structure.roadNumber,
    isBlockApplicable: structure.block,
    isTowerApplicable: structure.tower,
    isFlatApplicable: structure.flat,
    isHouseApplicable: structure.house,
    isPlotApplicable: structure.plot,
    isBlockOrTowerApplicable: structure.block || structure.tower,
  };
}
